import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import { defineView } from "foldkit/submodel";
import { toMessage } from "../../api/errors";
import type { Subscription } from "../../api/schemas/billing";
import { i18next } from "../../i18n";
import { busyAttributes, busySpinner } from "../../widgets/busy";
import type { Shared } from "../shared";
import { Message } from "./message";
import type { Model } from "./model";
import { canAct, canSubscribe } from "./update";
const t = (key: string, options: Record<string, unknown> = {}) =>
	i18next.t(`billing.${key}`, options);
const counts = (plan: { records: number; entries: number }) => {
	const n = new Intl.NumberFormat();
	return { records: n.format(plan.records), entries: n.format(plan.entries) };
};
export const view = defineView<Model, Message, Shared>((model, shared, h) => {
	const content = (subscription: Subscription) =>
		h.div(
			[h.Class("space-y-5")],
			[
				h.p([h.Role("status")], [t(`status.${subscription.status}`)]),
				h.div(
					[h.Class("grid gap-4 md:grid-cols-2")],
					[
						h.section(
							[h.Class("card bg-base-200 p-6 space-y-3")],
							[
								h.h2([h.Class("text-xl font-semibold")], [t("free")]),
								h.p([], [t("freeDescription")]),
								h.p([], [t("limits", counts(subscription.plan.free))]),
								h.p([], [t("freeUploads")]),
							],
						),
						h.section(
							[h.Class("card border border-primary p-6 space-y-3")],
							[
								h.h2([h.Class("text-xl font-semibold")], [t("paid")]),
								h.p(
									[h.Class("text-3xl font-semibold")],
									[
										t("price", {
											price: new Intl.NumberFormat(undefined, {
												style: "currency",
												currency: subscription.plan.currency,
											}).format(subscription.plan.priceCents / 100),
										}),
									],
								),
								h.p([], [t("paidDescription")]),
								h.p([h.Class("font-semibold")], [t("unlimited")]),
								h.p([], [t("plusUploads")]),
								h.p(
									[h.Class("text-sm text-base-content/70")],
									[t("fairUse", counts(subscription.plan.paid))],
								),
								...(canSubscribe(subscription)
									? [
											h.button(
												[
													h.Type("button"),
													h.Class("btn btn-primary"),
													h.AriaLabel(
														t(
															subscription.status === "pending"
																? "continueCheckout"
																: "subscribe",
														),
													),
													...busyAttributes(
														h,
														model.busy,
														!canAct(model, shared) || !subscription.enabled,
													),
													h.OnClick(Message.ClickedSubscribe()),
												],
												[
													busySpinner(h, model.busy),
													t(
														model.busy
															? "opening"
															: subscription.status === "pending"
																? "continueCheckout"
																: "subscribe",
													),
												],
											),
										]
									: []),
								...(subscription.canOpenPortal
									? [
											h.button(
												[
													h.Type("button"),
													h.Class(
														canSubscribe(subscription)
															? "btn btn-outline"
															: "btn btn-primary",
													),
													h.AriaLabel(t("manage")),
													...busyAttributes(
														h,
														model.busy,
														!canAct(model, shared),
													),
													h.OnClick(Message.ClickedPortal()),
												],
												[
													busySpinner(h, model.busy),
													t(model.busy ? "opening" : "manage"),
												],
											),
										]
									: []),
							],
						),
					],
				),
				...(subscription.currentPeriodEnd
					? [
							h.p(
								[],
								[
									t(
										subscription.status === "scheduled_cancel"
											? "ends"
											: "periodEnd",
										{
											date: new Date(
												subscription.currentPeriodEnd,
											).toLocaleDateString(),
										},
									),
								],
							),
						]
					: []),
				...(!subscription.enabled ? [h.p([], [t("unavailable")])] : []),
				...(!subscription.canManage ? [h.p([], [t("ownerOnly")])] : []),
				...(subscription.status === "pending"
					? [h.p([h.Class("text-sm text-base-content/70")], [t("pendingHelp")])]
					: []),
			],
		);
	return h.div(
		[
			h.DataAttribute("testid", "page-CreditUsage"),
			h.Class("container mx-auto max-w-3xl px-4 py-8 space-y-6"),
		],
		[
			h.h1([h.Class("text-3xl font-bold")], [t("title")]),
			h.p([], [t("description")]),
			...(Option.isSome(shared.asOf)
				? [h.p([h.Role("status")], [t("historical")])]
				: []),
			AsyncData.match(model.subscription, {
				onIdle: () => h.empty,
				onLoading: () => h.p([h.Role("status")], [t("loading")]),
				onFailure: (error) => h.p([h.Role("alert")], [toMessage(error)]),
				onSuccess: content,
				onRefreshing: content,
				onStale: ({ error, data }) =>
					h.div(
						[],
						[h.p([h.Role("alert")], [toMessage(error)]), content(data)],
					),
			}),
			Option.match(model.error, {
				onNone: () => h.empty,
				onSome: (error) => h.p([h.Role("alert")], [error]),
			}),
			h.div(
				[h.Class("flex flex-wrap gap-4 items-center")],
				[
					h.a(
						[h.Href("mailto:support@rhysomn.com"), h.Class("link")],
						[t("support")],
					),
					h.a([h.Href("/audit"), h.Class("link")], [t("audit")]),
				],
			),
		],
	);
});
